import "@/App.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { Toaster, toast } from "sonner";
import LoginPage from "./pages/LoginPage";
import PublicDemoLanding from "./pages/PublicDemoLanding";
import PublicDemoBanner from "./components/PublicDemoBanner";
import ExpansionLanding from "./pages/ExpansionLanding";
import { PrivacyPolicy, TermsOfService } from "./pages/Legal";
import ManagerDailyEntry from "./pages/ManagerDailyEntry";
import ManagerHistory from "./pages/ManagerHistory";
import AdminDashboard from "./pages/AdminDashboard";
import ReviewLibrary from "./pages/ReviewLibrary";
import ReviewWorkspace from "./pages/ReviewWorkspace";
import ReviewPresentationView from "./pages/ReviewPresentationView";
import AdminCustomerExperience from "./pages/AdminCustomerExperience";
import AdminOutlets from "./pages/AdminOutlets";
import AdminManagers from "./pages/AdminManagers";
import AdminPortfolios from "./pages/AdminPortfolios";
import AdminChecklist from "./pages/AdminChecklist";
import AdminChecklistDashboard from "./pages/AdminChecklistDashboard";
import ManagerChecklist from "./pages/ManagerChecklist";
import AdminTickets from "./pages/AdminTickets";
import ManagerTickets from "./pages/ManagerTickets";
import AdminOpsCommandCentre from "./pages/AdminOpsCommandCentre";
import AdminComplianceIntelligence from "./pages/AdminComplianceIntelligence";
import AdminNotificationRules from "./pages/AdminNotificationRules";
import AdminTicketSettings from "./pages/AdminTicketSettings";
import ManagerOpsCommandCentre from "./pages/ManagerOpsCommandCentre";
import ManagerComplianceIntelligence from "./pages/ManagerComplianceIntelligence";
import AdminSettings from "./pages/AdminSettings";
import AdminMaintenance from "./pages/AdminMaintenance";
import AssetDetail from "./pages/AssetDetail";
import AdminReservations from "./pages/AdminReservations";
import AdminCustomers from "./pages/AdminCustomers";
import AdminLoyalty from "./pages/AdminLoyalty";
import AdminMembershipLeads from "./pages/AdminMembershipLeads";
import AdminDemoLeads from "./pages/AdminDemoLeads";
import SuperAdminDemoOps from "./pages/SuperAdminDemoOps";
import AdminMembershipContent from "./pages/AdminMembershipContent";
import GuestHub from "./pages/GuestHub";
import GuestHubMembership from "./pages/GuestHubMembership";
import AdminVoucherStudio from "./pages/AdminVoucherDesignStudio";
import AdminVoucherBuilder from "./pages/AdminVoucherBuilder";
import ManagerLoyalty from "./pages/ManagerLoyalty";
import GuestLoyaltyWallet from "./pages/GuestLoyaltyWallet";
import PublicVoucherPreview from "./pages/PublicVoucherPreview";
import GiftVoucherLanding from "./pages/GiftVoucherLanding";
import LoyaltyWelcomeLink from "./pages/LoyaltyWelcomeLink";
import GiftShortRedirect from "./pages/GiftShortRedirect";
import AdminOutletMenus from "./pages/AdminOutletMenus";
import ManagerMenuAvailability from "./pages/ManagerMenuAvailability";
import GuestMenu from "./pages/GuestMenu";
import AdminMarketing from "./pages/AdminMarketing";
import AdminRecipes from "./pages/AdminRecipes";
import ManagerRecipes from "./pages/ManagerRecipes";
import ManagerReservations from "./pages/ManagerReservations";
import ManagerMarketing from "./pages/ManagerMarketing";
import PettyCashModule from "./pages/PettyCashModule";
import PettyCashWallets from "./pages/PettyCashWallets";
import PettyCashReports from "./pages/PettyCashReports";
import PettyCashCategorySettings from "./pages/PettyCashCategorySettings";
import CxAdmin from "./pages/cx/CxAdmin";
import CxFeedback from "./pages/cx/CxFeedback";
import CxPrintQr from "./pages/cx/CxPrintQr";
import ExpansionIntelligenceHome from "./pages/ExpansionIntelligenceHome";
import CityIntelligence from "./pages/CityIntelligence";
import ActionCommCenter from "./pages/ActionCommCenter";
import StudioLogin from "./pages/StudioLogin";
import StudioHome from "./pages/StudioHome";
import StudioLanding from "./pages/StudioLanding";
import { StudioIngredientsPage, StudioRecipesPage, StudioPricingPage, StudioProductionPage, StudioLibraryPage } from "./pages/StudioModulePages";
import SuperAdminStudio from "./pages/SuperAdminStudio";
import SuperAdminAccess from "./pages/SuperAdminAccess";
import SuperAdminSecurity from "./pages/SuperAdminSecurity";
import SuperAiUsage from "./pages/SuperAiUsage";
import Layout from "./components/Layout";
import SuperAdminLayout from "./components/SuperAdminLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import NoAccessScreen from "./components/NoAccessScreen";
import FeatureDisabledScreen from "./components/FeatureDisabledScreen";
import RestrictedHome from "./components/RestrictedHome";
import { ADMIN_LIKE_ROLES, MANAGER_LIKE_ROLES, SCOPED_ADMIN_ROLES, moduleForPath, modulesForPath, pickLandingPath, pickFallbackForModule } from "./config/modules";

// iter167 — opt-in features default to `false` (hidden until super-admin
// enables for the tenant). All other features default to `true` (legacy
// modules retain access for existing tenants — safe migration invariant).
// iter183k — `expansion_intelligence` (Strategy section, premium add-on) is
// premium-OFF by default, same posture as Customer Pulse Index.
const OPT_IN_FEATURES = new Set(['custom_feedback_forms', 'expansion_intelligence', 'loyalty']);

import { CHILD_TO_PARENT } from './config/feature_parents';

function isTenantFeatureEnabled(tenantFeatures, feature) {
  if (!feature) return true;
  const v = tenantFeatures?.[feature];
  let baseEnabled;
  if (v === undefined) baseEnabled = !OPT_IN_FEATURES.has(feature);
  else baseEnabled = v !== false;
  if (!baseEnabled) return false;
  // iter180 — Parent cascade: child is disabled if its parent is OFF.
  const parent = CHILD_TO_PARENT[feature];
  if (parent && tenantFeatures?.[parent] === false) return false;
  return true;
}

function ProtectedRoute({ children, requiredRole, requiredFeature }) {
  const { user, loading, tenantFeatures, tenantFeaturesLoading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="animate-spin w-8 h-8 border-2 border-[#143D30] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  // iter178b — block the route shell until tenantFeatures has loaded so a
  // gated child component never mounts (and never fires API calls) only to
  // be unmounted milliseconds later when the feature payload comes back as
  // "disabled". Prevents transient 403 toasts on legitimately gated pages.
  if (requiredFeature && tenantFeaturesLoading && user.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="animate-spin w-8 h-8 border-2 border-[#143D30] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Studio chefs live in /studio/* — never let them touch admin/manager/super shells.
  if (user.role === 'studio_chef') {
    return <Navigate to="/studio/home" replace />;
  }

  // Studio users are restricted to the Expansion Intelligence workspace ONLY.
  // Any attempt to reach a non-EI admin/manager route is bounced back.
  if (user.role === 'studio_user') {
    if (location.pathname !== '/admin/expansion-intelligence') {
      return <Navigate to="/admin/expansion-intelligence" replace />;
    }
    return <Layout>{children}</Layout>;
  }

  // Phase 3B · Slice 3 · Loyalty Sales is restricted to /sales/loyalty
  // on mobile, and read-only /admin/loyalty on desktop. Any other
  // admin/manager URL redirects to their canonical Loyalty entry.
  if (user.role === 'loyalty_sales') {
    const path = location.pathname || '';
    const allowedLoyalty =
      path === '/sales/loyalty' ||
      path === '/admin/loyalty' ||
      path.startsWith('/admin/loyalty/');
    if (!allowedLoyalty) {
      const isMobile = typeof window !== 'undefined'
        && window.matchMedia('(max-width: 767px)').matches;
      return <Navigate to={isMobile ? '/sales/loyalty' : '/admin/loyalty'} replace />;
    }
  }

  const role = user.role;

  // Super-admin route guard.
  if (requiredRole === 'super_admin' && role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  // Admin route guard — only admin-like roles get the admin shell.
  // ITER308-B · Assignment-driven scoped-admin shell.
  //   Both `production_head` (legacy, was hardcoded) and the new
  //   `maintenance_manager` share the admin shell but see ONLY the
  //   admin routes whose gating module is in their `assigned_modules`.
  //   No more per-role hardcoded whitelist — one mechanism for both.
  //   ITER308-B compat: any legacy production_head without
  //   `assigned_modules` still gets the recipes/pricing/production
  //   trio (behaviour-identical migration).
  const LEGACY_PRODUCTION_HEAD_MODS = ['recipes', 'pricing', 'production'];
  if (requiredRole === 'admin' && SCOPED_ADMIN_ROLES.includes(role)) {
    const path = location.pathname || '';
    let mods = user.assigned_modules || [];
    if (mods.length === 0 && role === 'production_head') {
      mods = LEGACY_PRODUCTION_HEAD_MODS;
    }
    const gatingMods = modulesForPath(path);
    const allowed = gatingMods.length === 0
      || gatingMods.some((m) => mods.includes(m));
    if (!allowed) {
      const landing = pickLandingPath(user, tenantFeatures);
      return <Navigate to={landing || '/admin/dashboard'} replace />;
    }
  }
  if (requiredRole === 'admin' && !ADMIN_LIKE_ROLES.includes(role) && !SCOPED_ADMIN_ROLES.includes(role)) {
    // Loyalty Sales gets a read-only admin shell for /admin/loyalty* only.
    // Every other admin route is bounced back to their Loyalty entry.
    if (role === 'loyalty_sales') {
      const path = location.pathname || '';
      if (path === '/admin/loyalty' || path.startsWith('/admin/loyalty/')) {
        // fall through — render Layout with the Loyalty child.
      } else {
        return <Navigate to="/admin/loyalty" replace />;
      }
    } else {
      if (role === 'super_admin') return <Navigate to="/super/dashboard" replace />;
      // Try to send them to a manager-side equivalent of the same module.
      const needed = moduleForPath(location.pathname);
      const fallback = needed ? pickFallbackForModule(role, needed) : null;
      const landing = pickLandingPath(user, tenantFeatures);
      return <Navigate to={fallback || landing || '/'} replace />;
    }
  }

  // Manager route guard — only manager-like roles get the manager shell.
  if (requiredRole === 'manager' && !MANAGER_LIKE_ROLES.includes(role)) {
    if (role === 'super_admin') return <Navigate to="/super/dashboard" replace />;
    if (ADMIN_LIKE_ROLES.includes(role) || SCOPED_ADMIN_ROLES.includes(role)) {
      const needed = moduleForPath(location.pathname);
      const fallback = needed ? pickFallbackForModule(role, needed) : null;
      return <Navigate to={fallback || '/admin/dashboard'} replace />;
    }
    const landing = pickLandingPath(user, tenantFeatures);
    return landing ? <Navigate to={landing} replace /> : <NoAccessScreen />;
  }

  // Module-based gating for non-admin-like users.
  const isAdminLike = ADMIN_LIKE_ROLES.includes(role);
  // loyalty_sales is granted implicit `loyalty` module access via role.
  const isLoyaltySalesOnLoyalty = role === 'loyalty_sales'
    && (location.pathname === '/admin/loyalty' || location.pathname.startsWith('/admin/loyalty/'));
  if (!isAdminLike && !isLoyaltySalesOnLoyalty) {
    const gatingMods = modulesForPath(location.pathname);
    const mods = user.assigned_modules || [];
    const skipCheck = role === 'manager' && mods.length === 0; // legacy full-access
    // Path is gated AND user has none of its acceptable modules → deny.
    if (gatingMods.length > 0 && !skipCheck && !gatingMods.some(id => mods.includes(id))) {
      // Try to redirect to a module they DO have access to.
      const landing = pickLandingPath(user, tenantFeatures);
      // If pickLandingPath returns the SAME path the user is already on, we'd
      // loop — fall back to the access-denied screen instead.
      if (landing && landing !== location.pathname) {
        return <Navigate to={landing} replace />;
      }
      return (
        <Layout>
          <NoAccessScreen
            title="Module not assigned"
            message="You don't have access to this module yet. Please ask your administrator to assign it, then sign back in."
          />
        </Layout>
      );
    }
  }

  if (requiredRole === 'super_admin') {
    return <SuperAdminLayout>{children}</SuperAdminLayout>;
  }

  // iter167 — Tenant feature gate. When `requiredFeature` is declared on
  // the route AND the tenant has it explicitly disabled, render the
  // "Feature Disabled" fallback inside the standard Layout shell so the
  // user keeps full navigation but can't access the disabled module
  // (sidebar already hides it via the same flag — this is the direct-URL
  // defence). Super-admin always bypasses.
  if (requiredFeature && role !== 'super_admin'
      && !isTenantFeatureEnabled(tenantFeatures, requiredFeature)) {
    return (
      <Layout>
        <FeatureDisabledScreen feature={requiredFeature} />
      </Layout>
    );
  }

  // Sprint 2 Consolidation Part 8 · POS is a full-screen application.
  // The platform navigation strip must not render on /pos or /pos-demo.
  // Every other route keeps its Layout chrome.
  if (typeof window !== "undefined"
      && (window.location.pathname === "/pos"
          || window.location.pathname.startsWith("/pos/")
          || window.location.pathname === "/pos-demo")) {
    // /pos/settings and /pos/reports still show the platform Layout —
    // they are configuration and analytics surfaces, not the terminal
    // itself.
    if (window.location.pathname !== "/pos/settings"
        && window.location.pathname !== "/pos/reports") {
      return children;
    }
  }
  // Presentation Mode for Review & Discussion — full-screen, no Layout chrome.
  if (typeof window !== "undefined"
      && /^\/reviews\/[^/]+\/present\/?$/.test(window.location.pathname)) {
    return children;
  }
  return <Layout>{children}</Layout>;
}

function RootRedirect() {
  const { user, loading, tenantFeatures, tenantFeaturesLoading, getHeaders } = useAuth();
  const [explicitLanding, setExplicitLanding] = useState(undefined);  // undefined = not yet fetched
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    if (!user || tenantFeaturesLoading) return;
    if (user.role === 'super_admin' || user.role === 'production_head') {
      setExplicitLanding(null);
      return;
    }
    // iter179 — Ask the backend for the tenant's explicit landing_module
    // (validated against feature flags). If null, FE falls through to chain.
    axios.get(`${API}/tenant/welcome`, { headers: getHeaders() })
      .then((r) => setExplicitLanding(r.data?.landing_path || null))
      .catch(() => setExplicitLanding(null));
  }, [user, tenantFeaturesLoading, getHeaders, API]);

  if (loading || tenantFeaturesLoading || (user && explicitLanding === undefined)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="animate-spin w-8 h-8 border-2 border-[#143D30] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/welcome" replace />;
  if (user.role === 'super_admin') return <Navigate to="/super/dashboard" replace />;
  // Phase 3B · Slice 3 · Loyalty Sales landing:
  //   desktop → /admin/loyalty (full read-only Loyalty module)
  //   mobile  → /sales/loyalty (focused transactional workspace)
  if (user.role === 'loyalty_sales') {
    const isMobile = typeof window !== 'undefined'
      && window.matchMedia('(max-width: 767px)').matches;
    return <Navigate to={isMobile ? '/sales/loyalty' : '/admin/loyalty'} replace />;
  }
  // ITER308-B · Scoped-admin roles land on their first assigned module.
  if (user.role === 'production_head' && (user.assigned_modules || []).length === 0) {
    return <Navigate to="/admin/recipes" replace />;
  }
  if (user.role === 'maintenance_manager') {
    return <Navigate to="/admin/maintenance" replace />;
  }

  // iter179 — Prefer the tenant's explicit landing_module if the backend
  // validated it (feature still enabled, mapped to a real page).
  const landing = explicitLanding || pickLandingPath(user, tenantFeatures);
  // iter177 — When NO module is reachable for this tenant (everything
  // disabled), render the premium RestrictedHome instead of a redirect loop.
  if (!landing) {
    return (
      <Layout>
        <RestrictedHome />
      </Layout>
    );
  }
  return <Navigate to={landing} replace />;
}

import AdminPnL from './pages/AdminPnL';
import ManagerMaintenance from './pages/ManagerMaintenance';
import ManagerInsights from './pages/ManagerInsights';
import AdminInsights from './pages/AdminInsights';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminBranding from './pages/SuperAdminBranding';
import PublicFeedback from './pages/PublicFeedback';
import FeedbackThanks from './pages/FeedbackThanks';
import AdminInventory from './pages/AdminInventory';
import AdminOpeningStock from './pages/AdminOpeningStock';
import AdminOverrideRequests from './pages/AdminOverrideRequests';
import ManagerClosingStock from './pages/ManagerClosingStock';
import CostInsights from './pages/CostInsights';
import ManagerPnL from './pages/ManagerPnL';
import ManagerCustomerFeedback from './pages/ManagerCustomerFeedback';
import AdminProduction from './pages/AdminProduction';
import ManagerProduction from './pages/ManagerProduction';
import SuperAdminFeatures from './pages/SuperAdminFeatures';
import SuperAdminApify from './pages/SuperAdminApify';
// Sprint 1 · POS UI prototype (mock only, no backend, no feature flag)
import POSTerminal from './pages/POSTerminal';
import PosSettings from './pages/PosSettings';
import PosReports from './pages/PosReports';
import LoyaltySalesWorkspace from './pages/LoyaltySalesWorkspace';

// Sprint 6 correction 5 · Public /pos-demo route — reachable without
// any authentication. Renders the same POSTerminal component that
// the authenticated /super/pos/terminal route renders, with a demo
// flag that swaps the initial reducer state for a pre-seeded
// snapshot and shows a "Reset demo" banner. This route is
// intentionally PUBLIC and must NOT be gated by the upcoming tenant
// feature flag when the backend lands.
function PosDemoPage() {
  return <POSTerminal demo={true} />;
}

// Full-viewport guard for the POS terminal — replaces the standard
// ProtectedRoute→SuperAdminLayout stack because the terminal needs the
// entire 1366×768 canvas with no chrome. Uses the same `useAuth` hook
// and the same role-check pattern as the rest of the app.
// POS Sprint 1 · This is a tenant-facing route: any authenticated
// tenant user (admin / area_manager / manager / supervisor / cashier)
// reaches the shell, then the in-app PIN gate enforces which POS
// staff role can actually operate the terminal.
function PosTerminalGuard() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <POSTerminal />;
}


// Phase 3B · Slice 3 · Sales workspace guard.
// Full-viewport (no admin/manager Layout chrome). Allowed roles:
//   admin, area_manager, super_admin, loyalty_sales
// Any other role is bounced to the standard landing so we never leak
// the Sales UI to a non-sales operator.
const SALES_WORKSPACE_ROLES = ['admin', 'area_manager', 'super_admin', 'loyalty_sales'];
function SalesLoyaltyGuard() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!SALES_WORKSPACE_ROLES.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <LoyaltySalesWorkspace />;
}


// Phase 3B · Slice 3 · Mobile-admin redirect helper. On viewport
// widths < 768px, an admin-family user opening any /admin/loyalty*
// route is bounced to /sales/loyalty (transactional mobile UI).
// Desktop widths continue to render the full Admin Loyalty
// workspace. Uses window.matchMedia — NOT user-agent sniffing.
function AdminLoyaltyMobileRedirect({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (isMobile && user
        && (ADMIN_LIKE_ROLES.includes(user.role) || user.role === 'loyalty_sales')
        && !notified) {
      toast('Loyalty administration is available on desktop. Mobile is optimised for member creation and sales.',
            { duration: 5000 });
      setNotified(true);
    }
  }, [isMobile, user, notified]);

  if (isMobile && user
      && (ADMIN_LIKE_ROLES.includes(user.role) || user.role === 'loyalty_sales')) {
    return <Navigate to="/sales/loyalty" replace state={{ from: location }} />;
  }
  return children;
}

function App() {
  // Global: prevent mouse-wheel from changing focused number input values.
  // Protects qty/yield/kg/ml/ltr fields from accidental edits during page scroll.
  useEffect(() => {
    const onWheel = (e) => {
      const t = e.target;
      if (t && t.tagName === 'INPUT' && t.type === 'number' && document.activeElement === t) {
        t.blur();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <ErrorBoundary>
    <BrandingProvider>
    <AuthProvider>
      <Toaster position="top-right" richColors theme="light" />
      <BrowserRouter>
        <PublicDemoBanner />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/welcome" element={<ExpansionLanding />} />

          {/* Pass 3 · Public Demo Access (no auth) — OTP-gated entry
              into the Amber Kitchen read-only demo. */}
          <Route path="/demo" element={<PublicDemoLanding />} />

          {/* iter180f — Public legal pages (no auth, required by Google OAuth verification) */}
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/terms" element={<TermsOfService />} />

          {/* Public QR-feedback routes (no auth) */}
          <Route path="/feedback/:outletId/thanks" element={<FeedbackThanks />} />
          <Route path="/feedback/:outletId/:formType" element={<PublicFeedback />} />
          {/* iter165 — Hospitality Experience Engine (single-segment QR token) */}
          <Route path="/feedback/:token" element={<CxFeedback />} />

          {/* Sprint 6 correction 5 · Public POS demo · no login, no
              feature-flag gate, sample data resets on demand. */}
          <Route path="/pos-demo" element={<PosDemoPage />} />

          <Route path="/" element={<RootRedirect />} />
          <Route path="/super/dashboard" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminDashboard /></ProtectedRoute>
          } />
          <Route path="/super/branding" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminBranding /></ProtectedRoute>
          } />

          {/* Public Chef Studio (Phase 1) — separate auth & shell. Studio
              routes do NOT use ProtectedRoute; each page has its own
              StudioGuard that bounces non-studio users to /studio/login or
              their natural home. */}
          <Route path="/studio" element={<StudioLanding />} />
          <Route path="/studio/login" element={<StudioLogin />} />
          <Route path="/studio/home" element={<StudioHome />} />
          <Route path="/studio/ingredients" element={<StudioIngredientsPage />} />
          <Route path="/studio/recipes" element={<StudioRecipesPage />} />
          <Route path="/studio/pricing" element={<StudioPricingPage />} />
          <Route path="/studio/production" element={<StudioProductionPage />} />
          <Route path="/studio/library" element={<StudioLibraryPage />} />

          {/* Super Admin — Studio Control Center */}
          <Route path="/super/studio" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminStudio /></ProtectedRoute>
          } />
          <Route path="/super/access" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminAccess /></ProtectedRoute>
          } />
          <Route path="/super/security" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminSecurity /></ProtectedRoute>
          } />
          <Route path="/super/ai-usage" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAiUsage /></ProtectedRoute>
          } />
          <Route path="/manager/entry" element={
            <ProtectedRoute requiredRole="manager"><ManagerDailyEntry /></ProtectedRoute>
          } />
          <Route path="/manager/history" element={
            <ProtectedRoute requiredRole="manager"><ManagerHistory /></ProtectedRoute>
          } />
          <Route path="/manager/pnl" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="pnl_live"><ManagerPnL /></ProtectedRoute>
          } />
          <Route path="/manager/customer-feedback" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="customer_feedback"><ManagerCustomerFeedback /></ProtectedRoute>
          } />
          {/* Manager read-only Overview + Sales Analytics (Feb 2026).
              Reuses the exact Admin components via viewerMode='manager'.
              Backend enforces outlet scope from the user's assignment. */}
          <Route path="/manager" element={<Navigate to="/manager/overview" replace />} />
          {/* Review & Discussion V1 (Feb 2026) — Admin + Manager reuse same components */}
          <Route path="/admin/reviews" element={
            <ProtectedRoute requiredRole="admin"><ReviewLibrary viewerMode="admin" /></ProtectedRoute>
          } />
          <Route path="/admin/reviews/:id" element={
            <ProtectedRoute requiredRole="admin"><ReviewWorkspace viewerMode="admin" /></ProtectedRoute>
          } />
          <Route path="/manager/reviews" element={
            <ProtectedRoute requiredRole="manager"><ReviewLibrary viewerMode="manager" /></ProtectedRoute>
          } />
          <Route path="/manager/reviews/:id" element={
            <ProtectedRoute requiredRole="manager"><ReviewWorkspace viewerMode="manager" /></ProtectedRoute>
          } />
          <Route path="/reviews/:id/present" element={
            <ProtectedRoute><ReviewPresentationView /></ProtectedRoute>
          } />
          <Route path="/manager/overview" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="dashboard"><AdminDashboard viewerMode="manager" initialView="overview" /></ProtectedRoute>
          } />
          <Route path="/manager/sales-analytics" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="dashboard"><AdminDashboard viewerMode="manager" initialView="sales-analytics" /></ProtectedRoute>
          } />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="dashboard"><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/customer-experience" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="customer_feedback"><AdminCustomerExperience /></ProtectedRoute>
          } />
          <Route path="/admin/outlets" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="outlet_management"><AdminOutlets /></ProtectedRoute>
          } />
          <Route path="/admin/portfolios" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="outlet_management"><AdminPortfolios /></ProtectedRoute>
          } />
          <Route path="/admin/managers" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="user_management"><AdminManagers /></ProtectedRoute>
          } />
          <Route path="/admin/checklist" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="checklist"><AdminChecklist /></ProtectedRoute>
          } />
          <Route path="/admin/compliance" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="checklist"><AdminChecklistDashboard /></ProtectedRoute>
          } />
          {/* ITER295 — Operations Excellence · Smart Ticketing */}
          <Route path="/admin/tickets" element={
            <ProtectedRoute requiredRole="admin"><AdminTickets /></ProtectedRoute>
          } />
          {/* ITER296 — Operations Excellence · Command Centre + Intelligence */}
          <Route path="/admin/ops-command" element={
            <ProtectedRoute requiredRole="admin"><AdminOpsCommandCentre /></ProtectedRoute>
          } />
          <Route path="/admin/compliance-intelligence" element={
            <ProtectedRoute requiredRole="admin"><AdminComplianceIntelligence /></ProtectedRoute>
          } />
          {/* ITER296 Track B — Notification Rules Engine */}
          <Route path="/admin/notification-rules" element={
            <ProtectedRoute requiredRole="admin"><AdminNotificationRules /></ProtectedRoute>
          } />
          {/* ITER296.2 — Ticket Settings (routing table + priority SLA) */}
          <Route path="/admin/ticket-settings" element={
            <ProtectedRoute requiredRole="admin"><AdminTicketSettings /></ProtectedRoute>
          } />
          {/* ITER297 · Part 4 — Manager-shell ops surfaces */}
          <Route path="/manager/ops-command" element={
            <ProtectedRoute requiredRole="manager"><ManagerOpsCommandCentre /></ProtectedRoute>
          } />
          <Route path="/manager/compliance-intelligence" element={
            <ProtectedRoute requiredRole="manager"><ManagerComplianceIntelligence /></ProtectedRoute>
          } />
          <Route path="/admin/pnl" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="pnl_live"><AdminPnL /></ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute requiredRole="admin"><AdminSettings /></ProtectedRoute>
          } />
          <Route path="/admin/maintenance" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="maintenance"><AdminMaintenance /></ProtectedRoute>
          } />
          <Route path="/admin/maintenance/assets/:assetId" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="maintenance"><AssetDetail /></ProtectedRoute>
          } />
          {/* iter165 — Custom Feedback Forms (Hospitality Experience Engine).
              iter167 — gated by tenant feature `custom_feedback_forms`
              (default OFF — opt-in). Sidebar + route both honour the flag. */}
          <Route path="/admin/cx" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="custom_feedback_forms"><CxAdmin /></ProtectedRoute>
          } />
          <Route path="/admin/cx/print-qr" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="custom_feedback_forms"><CxPrintQr /></ProtectedRoute>
          } />
          {/* iter183k — Expansion Intelligence (Strategy section, premium add-on).
              Gated by tenant feature `expansion_intelligence` AND its parent
              `strategy_home` via the GROUP_PARENTS cascade. Default OFF. */}
          <Route path="/admin/expansion-intelligence" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="expansion_intelligence"><ExpansionIntelligenceHome /></ProtectedRoute>
          } />
          {/* ITER262 — City Intelligence (Phase 1 discovery flow, opt-in
              via same `expansion_intelligence` tenant feature). */}
          <Route path="/admin/expansion-intelligence/city" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="expansion_intelligence"><CityIntelligence /></ProtectedRoute>
          } />
          <Route path="/manager/checklist" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="checklist"><ManagerChecklist /></ProtectedRoute>
          } />
          {/* ITER295 — Manager Tickets */}
          <Route path="/manager/tickets" element={
            <ProtectedRoute requiredRole="manager"><ManagerTickets /></ProtectedRoute>
          } />
          <Route path="/manager/maintenance" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="maintenance"><ManagerMaintenance /></ProtectedRoute>
          } />
          <Route path="/admin/petty-cash" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="petty_cash"><PettyCashModule /></ProtectedRoute>
          } />
          <Route path="/admin/petty-cash/wallets" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="petty_cash"><PettyCashWallets /></ProtectedRoute>
          } />
          <Route path="/admin/petty-cash/reports" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="petty_cash"><PettyCashReports /></ProtectedRoute>
          } />
          <Route path="/admin/petty-cash/categories" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="petty_cash"><PettyCashCategorySettings /></ProtectedRoute>
          } />
          <Route path="/manager/petty-cash" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="petty_cash"><PettyCashModule /></ProtectedRoute>
          } />
          <Route path="/manager/petty-cash/wallets" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="petty_cash"><PettyCashWallets /></ProtectedRoute>
          } />
          <Route path="/manager/petty-cash/reports" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="petty_cash"><PettyCashReports /></ProtectedRoute>
          } />
          <Route path="/admin/action-comm" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="communication"><ActionCommCenter /></ProtectedRoute>
          } />
          <Route path="/manager/action-comm" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="communication"><ActionCommCenter /></ProtectedRoute>
          } />
          <Route path="/manager/reservations" element={
            <ProtectedRoute requiredRole="manager"><ManagerReservations /></ProtectedRoute>
          } />
          <Route path="/manager/marketing" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="marketing"><ManagerMarketing /></ProtectedRoute>
          } />
          <Route path="/manager/insights" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="reports"><ManagerInsights /></ProtectedRoute>
          } />
          <Route path="/admin/reservations" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="reservations_module"><AdminReservations /></ProtectedRoute>
          } />
          <Route path="/admin/customers" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="crm"><AdminCustomers /></ProtectedRoute>
          } />
          <Route path="/admin/loyalty" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="loyalty">
              <AdminLoyaltyMobileRedirect><AdminLoyalty /></AdminLoyaltyMobileRedirect>
            </ProtectedRoute>
          } />
          <Route path="/admin/loyalty/leads" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="loyalty">
              <AdminLoyaltyMobileRedirect><AdminMembershipLeads /></AdminLoyaltyMobileRedirect>
            </ProtectedRoute>
          } />
          <Route path="/admin/demo-leads" element={
            <ProtectedRoute requiredRole="admin"><AdminDemoLeads /></ProtectedRoute>
          } />
          <Route path="/super/demo-leads" element={
            <ProtectedRoute requiredRole="super_admin"><AdminDemoLeads /></ProtectedRoute>
          } />
          <Route path="/super/demo" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminDemoOps /></ProtectedRoute>
          } />
          <Route path="/admin/loyalty/membership-content" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="loyalty">
              <AdminLoyaltyMobileRedirect><AdminMembershipContent /></AdminLoyaltyMobileRedirect>
            </ProtectedRoute>
          } />
          {/* Phase-1 closeout · Old Voucher Studio route retired.
              Redirects to canonical Voucher Types area. The
              AdminVoucherDesignStudio component is preserved in the
              codebase but no longer routed for normal navigation. */}
          <Route path="/admin/loyalty/voucher-studio" element={
            <Navigate to="/admin/loyalty" replace />
          } />
          <Route path="/admin/loyalty/voucher-builder" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="loyalty">
              <AdminLoyaltyMobileRedirect><AdminVoucherBuilder /></AdminLoyaltyMobileRedirect>
            </ProtectedRoute>
          } />
          <Route path="/admin/loyalty/voucher-builder/:id" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="loyalty">
              <AdminLoyaltyMobileRedirect><AdminVoucherBuilder /></AdminLoyaltyMobileRedirect>
            </ProtectedRoute>
          } />
          {/* Phase 3B · Slice 3 · Loyalty Sales Workspace (mobile-first,
              transactional). Bypasses the admin/manager Layout so the
              screen is full-viewport. Role gate lives inside
              SalesLoyaltyGuard. */}
          <Route path="/sales/loyalty" element={<SalesLoyaltyGuard />} />
          <Route path="/manager/loyalty" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="loyalty"><ManagerLoyalty /></ProtectedRoute>
          } />
          <Route path="/loyalty/:slug" element={<GuestLoyaltyWallet />} />
          {/* Phase 2A · Admin-only design preview (auth-gated in
              PublicVoucherPreview via /api/admin/loyalty/voucher-preview). */}
          <Route path="/admin/voucher-preview/:vtId" element={
            <ProtectedRoute allowedRoles={["admin","super_admin"]}>
              <PublicVoucherPreview />
            </ProtectedRoute>
          } />
          <Route path="/loyalty/welcome/:token" element={<LoyaltyWelcomeLink />} />
          <Route path="/loyalty/gift/:token" element={<GiftVoucherLanding />} />
          <Route path="/g/:code" element={<GiftShortRedirect />} />
          <Route path="/loyalty/:slug/manager" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="loyalty"><ManagerLoyalty /></ProtectedRoute>
          } />
          {/* Digital Menu — public guest routes (no auth) */}
          <Route path="/menu/:slug" element={<GuestMenu />} />
          <Route path="/menu/:slug/:menuId" element={<GuestMenu />} />
          {/* Guest Hub — public NFC landing (no auth) */}
          <Route path="/hub/:slug" element={<GuestHub />} />
          <Route path="/hub/:slug/membership" element={<GuestHubMembership />} />
          {/* Digital Menu — admin management (per outlet) */}
          <Route path="/admin/outlets/:outletId/menus" element={
            <ProtectedRoute requiredRole="admin"><AdminOutletMenus /></ProtectedRoute>
          } />
          {/* iter280 — Manager Menu Availability (86'd items) */}
          <Route path="/manager/menu-availability" element={
            <ProtectedRoute requiredRole="manager"><ManagerMenuAvailability /></ProtectedRoute>
          } />
          <Route path="/admin/marketing" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="marketing"><AdminMarketing /></ProtectedRoute>
          } />
          <Route path="/admin/insights" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="financial_dashboard"><AdminInsights /></ProtectedRoute>
          } />
          <Route path="/admin/inventory" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="inventory_module"><AdminInventory /></ProtectedRoute>
          } />
          <Route path="/admin/opening-stock" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="inventory_module"><AdminOpeningStock /></ProtectedRoute>
          } />
          <Route path="/admin/override-requests" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="approvals"><AdminOverrideRequests /></ProtectedRoute>
          } />
          <Route path="/manager/kitchen" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="kitchen_closing"><ManagerClosingStock categoryGroup="kitchen" /></ProtectedRoute>
          } />
          <Route path="/manager/bar" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="bar_closing"><ManagerClosingStock categoryGroup="bar" /></ProtectedRoute>
          } />
          <Route path="/manager/cost-insights" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="cost_insights"><CostInsights /></ProtectedRoute>
          } />
          <Route path="/manager/recipes" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="recipe_module"><ManagerRecipes /></ProtectedRoute>
          } />
          <Route path="/admin/cost-insights" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="cost_insights"><CostInsights isAdmin /></ProtectedRoute>
          } />
          <Route path="/admin/recipes" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="recipe_module"><AdminRecipes /></ProtectedRoute>
          } />
          <Route path="/admin/production" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="production_planner"><AdminProduction /></ProtectedRoute>
          } />
          <Route path="/manager/production" element={
            <ProtectedRoute requiredRole="manager" requiredFeature="production_planner"><ManagerProduction /></ProtectedRoute>
          } />
          <Route path="/super/features" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminFeatures /></ProtectedRoute>
          } />
          <Route path="/super/apify" element={
            <ProtectedRoute requiredRole="super_admin"><SuperAdminApify /></ProtectedRoute>
          } />
          {/* POS Sprint 1 · Tenant-facing terminal, gated on the `pos`
              feature flag. Rendered outside Layout for the full-viewport
              terminal chrome. Staff auth is separate from the platform
              JWT — see StaffLoginScreen for the flow. Reachable by any
              authenticated tenant user; the PIN gate then decides which
              POS staff role can operate the terminal. */}
          <Route path="/pos" element={
            <ProtectedRoute requiredFeature="pos"><PosTerminalGuard /></ProtectedRoute>
          } />
          {/* POS Settings · Staff management surface, restricted to
              tenant admins (mirrors the POS "outlet_admin" role gate). */}
          <Route path="/pos/settings" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="pos"><PosSettings /></ProtectedRoute>
          } />
          {/* POS Reports — existing working reports + Advanced Analytics
              (Coming Soon) catalogue. See /app/frontend/src/pages/PosReports.js */}
          <Route path="/pos/reports" element={
            <ProtectedRoute requiredRole="admin" requiredFeature="pos"><PosReports /></ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </BrandingProvider>
    </ErrorBoundary>
  );
}

export default App;
